import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  HiOutlineViewGrid,
  HiOutlineShoppingBag,
  HiOutlineUsers,
  HiOutlineCube,
  HiOutlineChartBar,
  HiOutlineLogout,
  HiOutlinePhotograph,
  HiOutlineCollection,
  HiOutlineDocumentText,
  HiOutlineStar,
  HiOutlineCog,
  HiOutlineQuestionMarkCircle,
  HiOutlinePencilAlt,
  HiOutlineBadgeCheck,
  HiOutlineCurrencyDollar,
  HiOutlineClipboardCheck,
  HiOutlineMail,
  HiOutlineUserGroup,
  HiOutlineCash,
  HiOutlineCalendar,
  HiOutlineLightningBolt,
  HiOutlineChatAlt2,
  HiOutlineShieldCheck,
  HiOutlineTicket,
} from "react-icons/hi";
import { logoutUser } from "../../../redux/actions/user";
import { toast } from "react-toastify";

const AdminSideBar = ({ active, onItemClick, isMobileOverlay = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(logoutUser());
    toast.success("Logged out successfully!");
    navigate("/");
    if (onItemClick) onItemClick();
  };

  const menuItems = [
    { id: 1, name: "Dashboard", icon: HiOutlineViewGrid, url: "/admin/dashboard" },
    { id: 2, name: "Orders", icon: HiOutlineShoppingBag, url: "/admin-orders" },
    { id: 3, name: "Products", icon: HiOutlineCube, url: "/admin-products" },
    { id: 4, name: "Pending Products", icon: HiOutlineClipboardCheck, url: "/admin-pending-products" },
    { id: 5, name: "Categories", icon: HiOutlineCollection, url: "/admin-categories" },
    { id: 6, name: "Users", icon: HiOutlineUsers, url: "/admin-users" },
    { id: 7, name: "Vendors", icon: HiOutlineUserGroup, url: "/admin-sellers" },
    { id: 8, name: "Vendor Approvals", icon: HiOutlineBadgeCheck, url: "/admin-vendor-approvals" },
    { id: 9, name: "Vendor Payments", icon: HiOutlineCurrencyDollar, url: "/admin-vendor-payments" },
    { id: 10, name: "Withdraw Requests", icon: HiOutlineCash, url: "/admin-withdraw-request" },
    { id: 11, name: "Events", icon: HiOutlineCalendar, url: "/admin-events" },
    { id: 12, name: "Reviews", icon: HiOutlineStar, url: "/admin-reviews" },
    { id: 13, name: "Home Banner", icon: HiOutlinePhotograph, url: "/admin-banner" },
    { id: 14, name: "Blog", icon: HiOutlinePencilAlt, url: "/admin-blog" },
    { id: 15, name: "Analytics", icon: HiOutlineChartBar, url: "/admin/analytics" },
    { id: 16, name: "Legal Pages", icon: HiOutlineDocumentText, url: "/admin-legal-pages" },
    { id: 17, name: "Email Templates", icon: HiOutlineMail, url: "/admin-email-templates" },
    { id: 18, name: "FAQ", icon: HiOutlineQuestionMarkCircle, url: "/admin-faq" },
    { id: 19, name: "Site Settings", icon: HiOutlineCog, url: "/admin-site-settings" },
    { id: 20, name: "Services / Events", icon: HiOutlineTicket, url: "/admin-services" },
    { id: 21, name: "Offer Management", icon: HiOutlineChatAlt2, url: "/admin-offer-management" },
    { id: 22, name: "Auction Management", icon: HiOutlineLightningBolt, url: "/admin-auction-management" },
    { id: 23, name: "Verification", icon: HiOutlineShieldCheck, url: "/admin-verification" },
  ];

  return (
    <div
      className={`w-full h-full flex flex-col overflow-hidden ${
        isMobileOverlay ? "bg-transparent" : "bg-white shadow-sm border-r border-gray-200"
      }`}
    >
      <div className={`flex-1 overflow-y-auto ${isMobileOverlay ? "p-4 pt-3" : "p-3"}`}>
        <div className="space-y-1">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = active === item.id;
            return (
              <Link
                key={item.id}
                to={item.url}
                onClick={onItemClick}
                className={`
                  relative flex items-center rounded-lg font-medium transition-all duration-200 group
                  ${isMobileOverlay ? "px-3 py-3 gap-3" : "px-3 py-[9px] gap-3"}
                  ${isActive
                    ? "bg-primary-50 border border-primary-200/50 shadow-sm"
                    : "hover:bg-gray-50 border border-transparent hover:border-gray-200/50"
                  }
                `}
                title={item.name}
              >
                <div
                  className={`
                    flex items-center justify-center rounded-lg w-9 h-9 transition-all duration-200
                    ${isActive
                      ? "bg-primary-500 text-white shadow-sm"
                      : "bg-gray-100 text-gray-500 group-hover:bg-primary-100 group-hover:text-primary-600"
                    }
                  `}
                >
                  <IconComponent className="h-[18px] w-[18px]" />
                </div>
                <span
                  className={`
                    text-sm font-medium transition-colors truncate
                    ${isMobileOverlay ? "" : "hidden 800px:block"}
                    ${isActive ? "text-primary-700" : "text-gray-700 group-hover:text-primary-600"}
                  `}
                >
                  {item.name}
                </span>
                {isActive && (
                  <div className="absolute right-2 w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      <div className={`flex-shrink-0 border-t border-gray-100 ${isMobileOverlay ? "p-4" : "p-3"} ${isMobileOverlay ? "block" : "hidden 800px:block"}`}>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 hover:text-red-700 transition-all duration-200"
        >
          <div className="flex items-center justify-center rounded-lg w-9 h-9 bg-red-100">
            <HiOutlineLogout className="w-[18px] h-[18px]" />
          </div>
          <span className="text-sm font-medium">Logout</span>
        </button>
        <p className="text-center text-xs text-gray-400 mt-3">Positive Moments Admin</p>
      </div>
    </div>
  );
};

export default AdminSideBar;
